import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';


const INTEGRATIONS = [
  // Payments
  {
    id: 'stripe', name: 'Stripe', category: 'payments',
    description: 'Payment processing, subscriptions, and billing management. Accept payments from customers.',
    icon: 'CreditCard', iconBg: 'bg-violet-100 dark:bg-violet-900/30', iconColor: 'text-violet-600 dark:text-violet-400',
    status: 'connected', badge: 'Core',
  },
  // Communication
  {
    id: 'resend', name: 'Resend', category: 'communication',
    description: 'Transactional email delivery for notifications, invoices, review requests, and automated campaigns.',
    icon: 'Send', iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400',
    status: 'connected', badge: 'Core',
  },
  {
    id: 'twilio', name: 'Twilio SMS', category: 'communication',
    description: 'SMS messaging for review requests, job reminders, and customer follow-ups directly from CRM.',
    icon: 'MessageSquare', iconBg: 'bg-red-100 dark:bg-red-900/30', iconColor: 'text-red-600 dark:text-red-400',
    status: 'connected',
  },
  // Marketing
  {
    id: 'mailchimp', name: 'Mailchimp', category: 'marketing',
    description: 'Sync your CRM contacts with Mailchimp to run email marketing campaigns and automations.',
    icon: 'Mail', iconBg: 'bg-yellow-100 dark:bg-yellow-900/30', iconColor: 'text-yellow-600 dark:text-yellow-400',
    status: 'available',
  },
  {
    id: 'google-business', name: 'Google Business Profile', category: 'marketing',
    description: 'Pull Google reviews into your CRM, send automated review requests, and respond to customers.',
    icon: 'Star', iconBg: 'bg-yellow-100 dark:bg-yellow-900/30', iconColor: 'text-yellow-600 dark:text-yellow-400',
    status: 'available',
  },
  {
    id: 'google-ads', name: 'Google Ads', category: 'marketing',
    description: 'Create and manage search campaigns with AI-generated copy. Track ROI per campaign in CRM.',
    icon: 'Search', iconBg: 'bg-orange-100 dark:bg-orange-900/30', iconColor: 'text-orange-600 dark:text-orange-400',
    status: 'oauth', oauthKey: 'google', tokenField: 'google_ads_refresh_token',
  },
  {
    id: 'meta-ads', name: 'Meta Ads', category: 'marketing',
    description: 'Launch and manage Facebook & Instagram ad campaigns. Auto-import leads from Meta Lead Ads.',
    icon: 'Facebook', iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400',
    status: 'oauth', oauthKey: 'meta', tokenField: 'meta_access_token',
  },
  {
    id: 'tiktok-ads', name: 'TikTok Ads', category: 'marketing',
    description: 'Run TikTok ad campaigns and automatically import leads into your CRM pipeline.',
    icon: 'Video', iconBg: 'bg-pink-100 dark:bg-pink-900/30', iconColor: 'text-pink-600 dark:text-pink-400',
    status: 'coming_soon',
  },
  // Calendar
  {
    id: 'google-calendar', name: 'Google Calendar', category: 'calendar',
    description: 'Sync job appointments, follow-ups, and activities with Google Calendar automatically.',
    icon: 'Calendar', iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400',
    status: 'available',
  },
  // Accounting
  {
    id: 'quickbooks', name: 'QuickBooks', category: 'accounting',
    description: 'Sync invoices, payments, expenses, and customer data between Wilcart CRM and QuickBooks.',
    icon: 'BookOpen', iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400',
    status: 'coming_soon',
  },
];

const CATEGORIES = [
  { id: 'all', label: 'All', icon: 'Grid3X3' },
  { id: 'payments', label: 'Payments', icon: 'CreditCard' },
  { id: 'communication', label: 'Communication', icon: 'MessageSquare' },
  { id: 'marketing', label: 'Marketing', icon: 'TrendingUp' },
  { id: 'calendar', label: 'Calendar', icon: 'Calendar' },
  { id: 'accounting', label: 'Accounting', icon: 'BookOpen' },
];

const STATUS_BADGE = {
  connected: { label: 'Connected', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', dot: 'bg-green-500' },
  available: { label: 'Available', color: 'bg-muted text-muted-foreground', dot: 'bg-gray-400' },
  oauth: { label: 'Connect', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
  coming_soon: { label: 'Coming Soon', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-500' },
};

const IntegrationCard = ({ integration, onConnect }) => {
  const cfg = STATUS_BADGE?.[integration?.status] || STATUS_BADGE?.available;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-xl p-5 flex flex-col hover:shadow-elevation-2 transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${integration?.iconBg}`}>
          <Icon name={integration?.icon} size={22} className={integration?.iconColor} />
        </div>
        <div className="flex items-center space-x-2">
          {integration?.badge && (
            <span className="text-xs font-semibold px-2 py-0.5 bg-primary/10 text-primary rounded-full">{integration?.badge}</span>
          )}
          <span className={`flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded-full ${cfg?.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot}`} />
            <span>{cfg?.label}</span>
          </span>
        </div>
      </div>
      <h3 className="font-semibold text-card-foreground mb-1">{integration?.name}</h3>
      <p className="text-sm text-muted-foreground flex-1 leading-relaxed">{integration?.description}</p>
      <div className="mt-4">
        {integration?.status === 'connected' ? (
          <div className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400">
            <Icon name="CheckCircle" size={15} />
            <span>Active & Connected</span>
          </div>
        ) : integration?.status === 'coming_soon' ? (
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={15} />
            <span>Coming soon</span>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => onConnect(integration)} className="w-full">
            {integration?.status === 'oauth' ? 'Connect Account' : 'Set Up'}
          </Button>
        )}
      </div>
    </motion.div>
  );
};

const IntegrationsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const { profile } = useAuth();

  const filtered = INTEGRATIONS?.filter(i => {
    const matchCat = activeCategory === 'all' || i?.category === activeCategory;
    const matchSearch = !search || i?.name?.toLowerCase()?.includes(search?.toLowerCase()) || i?.description?.toLowerCase()?.includes(search?.toLowerCase());
    return matchCat && matchSearch;
  });

  const connectedCount = INTEGRATIONS?.filter(i => i?.status === 'connected')?.length;

  const handleConnect = (integration) => {
    if (integration?.status === 'oauth') {
      if (integration?.oauthKey === 'google') {
        window.open(`https://accounts.google.com/o/oauth2/v2/auth?client_id=${import.meta.env?.VITE_GOOGLE_ADS_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location?.origin + '/integrations')}&response_type=code&scope=https://www.googleapis.com/auth/adwords&access_type=offline`, '_blank');
      } else if (integration?.oauthKey === 'meta') {
        window.open(`https://www.facebook.com/v18.0/dialog/oauth?client_id=${import.meta.env?.VITE_META_APP_ID}&redirect_uri=${encodeURIComponent(window.location?.origin + '/integrations')}&scope=ads_management,leads_retrieval`, '_blank');
      }
    } else {
      window.open('/settings', '_self');
    }
  };

  return (
    <>
      <Helmet>
        <title>Integrations - Wilcart CRM</title>
        <meta name="description" content="Connect your tools and services to Wilcart CRM." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-8">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Integrations</h1>
                  <p className="text-muted-foreground mt-1">Connect the tools that power your service business</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">{connectedCount} Connected</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Search */}
            <div className="relative mb-6">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input type="text" value={search} onChange={e => setSearch(e?.target?.value)}
                placeholder="Search integrations..."
                className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>

            {/* Category Filter */}
            <div className="flex space-x-2 overflow-x-auto pb-2 mb-6">
              {CATEGORIES?.map(cat => (
                <button key={cat?.id} onClick={() => setActiveCategory(cat?.id)}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === cat?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}>
                  <Icon name={cat?.icon} size={14} />
                  <span>{cat?.label}</span>
                </button>
              ))}
            </div>

            {/* Grid */}
            {filtered?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Icon name="Puzzle" size={40} className="text-muted-foreground mb-3" />
                <p className="font-medium text-foreground">No integrations found</p>
                <p className="text-sm text-muted-foreground mt-1">Try a different search or category</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered?.map(integration => (
                  <IntegrationCard key={integration?.id} integration={integration} onConnect={handleConnect} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default IntegrationsPage;